import React, { useState, useEffect } from "react";
import {
  Container, Heading, VStack, Box, FormLabel, Textarea, Input,
  Button, Text, Spinner, Alert, AlertIcon, useToast, Divider, Badge
} from "@chakra-ui/react";
import {
  supabase,
  useLandingContentQuery, useUpdateLandingContentMutation,
  useLandingSectionsQuery, useUpdateLandingSectionMutation,
} from "../app/api";

const CONTENT_LABELS = {
  name:         { et: "Nimi (Markdown)",               en: "Name (Markdown)" },
  intro:        { et: "Sissejuhatav tekst (Markdown)",  en: "Intro text (Markdown)" },
  start_button: { et: "Alusta-nupu tekst",              en: "Start button text" },
  back_button:  { et: "Tagasi-nupu tekst",              en: "Back button text" },
};
const MULTILINE_KEYS = new Set(["name", "intro"]);

const ContentRow = ({ row, onSave }) => {
  const [et, setEt] = useState(row.value_et);
  const [en, setEn] = useState(row.value_en);
  const isMultiline = MULTILINE_KEYS.has(row.key);
  const dirty = et !== row.value_et || en !== row.value_en;
  const label = CONTENT_LABELS[row.key] || { et: row.key, en: row.key };

  useEffect(() => { setEt(row.value_et); setEn(row.value_en); }, [row.value_et, row.value_en]);

  return (
    <Box w="100%" p={5} borderWidth={1} borderRadius="md" bg="white">
      <Text fontWeight="bold" mb={3} textTransform="capitalize">
        {row.key}
        {dirty && <Badge ml={2} colorScheme="orange">Salvestamata</Badge>}
      </Text>
      <VStack align="stretch" spacing={3}>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">{label.et} (ET)</FormLabel>
          {isMultiline
            ? <Textarea value={et} onChange={e => setEt(e.target.value)} rows={5} fontFamily="mono" fontSize="sm" />
            : <Input value={et} onChange={e => setEt(e.target.value)} />}
        </Box>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">{label.en} (EN)</FormLabel>
          {isMultiline
            ? <Textarea value={en} onChange={e => setEn(e.target.value)} rows={5} fontFamily="mono" fontSize="sm" />
            : <Input value={en} onChange={e => setEn(e.target.value)} />}
        </Box>
        <Button colorScheme="teal" size="sm" alignSelf="flex-end" isDisabled={!dirty}
          onClick={() => onSave(row.key, et, en)}>
          Salvesta
        </Button>
      </VStack>
    </Box>
  );
};

const SectionRow = ({ section, onSave }) => {
  const [labelEt, setLabelEt] = useState(section.label_et);
  const [labelEn, setLabelEn] = useState(section.label_en);
  const [contentEt, setContentEt] = useState(section.content_et);
  const [contentEn, setContentEn] = useState(section.content_en);
  const dirty = labelEt !== section.label_et || labelEn !== section.label_en ||
                contentEt !== section.content_et || contentEn !== section.content_en;

  useEffect(() => {
    setLabelEt(section.label_et); setLabelEn(section.label_en);
    setContentEt(section.content_et); setContentEn(section.content_en);
  }, [section]);

  return (
    <Box w="100%" p={5} borderWidth={1} borderRadius="md" bg="white">
      <Text fontWeight="bold" mb={3}>
        #{section.order} — {section.label_et || section.label_en}
        {dirty && <Badge ml={2} colorScheme="orange">Salvestamata</Badge>}
        {!section.active && <Badge ml={2} colorScheme="gray">Peidetud</Badge>}
      </Text>
      <VStack align="stretch" spacing={3}>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">Nupu silt (ET)</FormLabel>
          <Input value={labelEt} onChange={e => setLabelEt(e.target.value)} />
        </Box>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">Nupu silt (EN)</FormLabel>
          <Input value={labelEn} onChange={e => setLabelEn(e.target.value)} />
        </Box>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">Sisu (ET) — Markdown</FormLabel>
          <Textarea value={contentEt} onChange={e => setContentEt(e.target.value)} rows={7} fontFamily="mono" fontSize="sm" />
        </Box>
        <Box>
          <FormLabel fontSize="sm" color="gray.600">Sisu (EN) — Markdown</FormLabel>
          <Textarea value={contentEn} onChange={e => setContentEn(e.target.value)} rows={7} fontFamily="mono" fontSize="sm" />
        </Box>
        <Button colorScheme="teal" size="sm" alignSelf="flex-end" isDisabled={!dirty}
          onClick={() => onSave(section.id, labelEt, labelEn, contentEt, contentEn)}>
          Salvesta
        </Button>
      </VStack>
    </Box>
  );
};

const AdminLanding = ({ session }) => {
  // Admin gate: RLS is the real barrier (profiles.is_admin), this mirrors it
  // in the UI. Fails closed if the column or profile row is missing.
  const [isAdmin, setIsAdmin] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", session?.user?.id)
      .single()
      .then(({ data, error }) => {
        if (!cancelled) setIsAdmin(!error && !!data?.is_admin);
      });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const { data: contentData, isLoading: contentLoading, isError: contentError } = useLandingContentQuery();
  const { data: sectionsData, isLoading: sectionsLoading, isError: sectionsError } = useLandingSectionsQuery();
  const [updateContent] = useUpdateLandingContentMutation();
  const [updateSection] = useUpdateLandingSectionMutation();
  const toast = useToast();

  const handleSaveContent = async (key, value_et, value_en) => {
    const result = await updateContent({ key, value_et, value_en });
    toast(result.error
      ? { title: "Viga salvestamisel", status: "error", duration: 3000 }
      : { title: "Salvestatud", status: "success", duration: 2000 });
  };

  const handleSaveSection = async (id, label_et, label_en, content_et, content_en) => {
    const result = await updateSection({ id, label_et, label_en, content_et, content_en });
    toast(result.error
      ? { title: "Viga salvestamisel", status: "error", duration: 3000 }
      : { title: "Salvestatud", status: "success", duration: 2000 });
  };

  const isLoading = contentLoading || sectionsLoading;
  const isError = contentError || sectionsError;

  if (isAdmin === null) {
    return <Container maxW="3xl" py={10}><Spinner /></Container>;
  }
  if (!isAdmin) {
    return (
      <Container maxW="3xl" py={10}>
        <Alert status="error"><AlertIcon />Sul puudub ligipääs sellele lehele.</Alert>
      </Container>
    );
  }

  return (
    <Container maxW="3xl" py={10}>
      <Heading size="lg" mb={2}>Avalehe sisu</Heading>
      <Text color="gray.500" mb={6}>Muuda avalehel kuvatavaid tekste. Markdown-väljad toetavad formateerimist.</Text>
      <Divider mb={6} />

      {isLoading && <Spinner />}
      {isError && <Alert status="error"><AlertIcon />Andmete laadimine ebaõnnestus.</Alert>}

      <VStack spacing={5}>
        {contentData?.map(row => (
          <ContentRow key={row.key} row={row} onSave={handleSaveContent} />
        ))}
      </VStack>

      {sectionsData?.length > 0 && (
        <>
          <Divider my={8} />
          <Heading size="md" mb={4}>Sektsioonid</Heading>
          <VStack spacing={5}>
            {sectionsData.map(section => (
              <SectionRow key={section.id} section={section} onSave={handleSaveSection} />
            ))}
          </VStack>
        </>
      )}
    </Container>
  );
};

export default AdminLanding;
